import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { LEGAL_VERSION } from '../../shared/legalVersion';
import { fetchPromoInfo } from '../services/api';
import { capturePromoFromUrl } from '../utils/promo';
import { getLastAuthMethod } from '../utils/authMethod';
import { listOAuthProviders, confirmEmailVerification, confirmEmailChange } from '../services/pocketbase';
import type { PromoCodePublicInfo } from '../../shared/types';
import { JOB_OPTIONS, INDUSTRY_OPTIONS, isJobOption, isIndustryOption } from '../../shared/types';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import TypologyShape, { type ShapeName } from './ui/TypologyShape';
import { PastelPulseRing } from './ui/PastelRing';
import { ThemeToggleButton, PaletteSwatches } from './ui/ThemeControls';

const ShaderBackground = lazy(() => import('./ui/ShaderBackground'));

type AuthView = 'login' | 'register' | 'reset' | 'check-email' | 'mfa-challenge' | 'otp' | 'otp-verify';

export default function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading, mfaRequired, recoveryMode, oauthError, signIn, signUp, signInWithGoogle, signInWithOtp, verifyEmailOtp, resetPassword, updatePassword, verifyMfaChallenge, signOut } = useAuth();
  const [view, setView] = useState<AuthView>('login');

  // ── Liens des emails PocketBase ───────────────────────────────────────
  // `/verify-email?token=…` (confirmation d'adresse) et
  // `/confirm-email-change?token=…` (nouvelle adresse, mot de passe demandé).
  // L'état pilote l'affichage, PAS l'URL : le jeton est retiré de la barre
  // d'adresse dès qu'il est lu, pour ne traîner ni dans l'historique ni dans
  // un partage de lien.
  const [verifyState, setVerifyState] = useState<'idle' | 'pending' | 'ok' | 'error'>('idle');
  const [emailChangeToken, setEmailChangeToken] = useState<string | null>(null);
  const [emailChangeState, setEmailChangeState] = useState<'idle' | 'ok' | 'error'>('idle');
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.pathname.startsWith('/verify-email')) {
      const token = url.searchParams.get('token');
      window.history.replaceState({}, '', '/');
      if (!token) { setVerifyState('error'); return; }
      setVerifyState('pending');
      void confirmEmailVerification(token)
        .then(() => setVerifyState('ok'))
        .catch(() => setVerifyState('error'));
      return;
    }
    if (url.pathname.startsWith('/confirm-email-change')) {
      const token = url.searchParams.get('token');
      window.history.replaceState({}, '', '/');
      if (token) setEmailChangeToken(token);
      else setEmailChangeState('error');
    }
  }, []);

  // Le bouton Google n'est rendu que si le fournisseur est réellement activé
  // côté PocketBase (GOOGLE_OAUTH_CLIENT_ID) : un bouton présent mais non
  // configuré échouerait au clic sans explication.
  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void listOAuthProviders().then((providers) => {
      if (!cancelled) setGoogleEnabled(providers.includes('google'));
    });
    return () => { cancelled = true; };
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryDone, setRecoveryDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  // Acceptation des CGU + Politique de confidentialité (RGPD art. 13 + preuve de consentement éclairé).
  // Obligatoire pour créer un compte. La date et la version sont écrites sur
  // users.terms_accepted_at / terms_version par le serveur, à la création.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Métier et secteur : obligatoires à l'inscription par email (listes fermées
  // de shared/types). Google et le code par email passent par ProfileGate.
  const [signupJob, setSignupJob] = useState('');
  const [signupIndustry, setSignupIndustry] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // Code promo scanné sur une carte de visite (?c=…) — pilote l'affichage de
  // l'offre et bascule d'office sur le parcours OTP, le plus rapide debout
  // dans un hall de salon. Le code lui-même est mis en attente par
  // capturePromoFromUrl() et encaissé après connexion (usePromoRedemption).
  const [promoCode, setPromoCode] = useState('');
  const [promoInfo, setPromoInfo] = useState<PromoCodePublicInfo | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpResent, setOtpResent] = useState(false);
  // Méthode employée la dernière fois sur cet appareil — sert à orienter le
  // visiteur inscrit au salon, qui n'a jamais eu de mot de passe. Lu une seule
  // fois : la valeur ne change pas pendant que l'écran est affiché.
  const [lastMethod] = useState(() => getLastAuthMethod());
  // Mode pastel : pas de fond shader, l'attente et la connexion se peignent
  // sur le crème de la charte (cf. PastelAuthLayout en bas de fichier).
  const { theme } = useTheme();
  const pastel = theme === 'pastel';

  useEffect(() => {
    const code = capturePromoFromUrl();
    if (!code) return;
    setPromoCode(code);
    // On bascule sur l'OTP avant même de savoir ce que vaut le code : si le
    // lookup échoue ou traîne, le visiteur a déjà le bon formulaire sous les
    // yeux. Le bandeau d'offre s'affichera dès la réponse.
    setView(v => (v === 'login' ? 'otp' : v));
    let cancelled = false;
    fetchPromoInfo(code).then(info => {
      if (!cancelled) setPromoInfo(info);
    });
    return () => { cancelled = true; };
  }, []);

  // Loading state
  if (isLoading) {
    if (pastel) {
      return (
        <div className="p-shell relative min-h-screen flex items-center justify-center overflow-hidden">
          <PastelPulseRing size={72} />
        </div>
      );
    }
    return (
      <div className="shader relative min-h-screen flex items-center justify-center overflow-hidden">
        <Suspense fallback={<div className="fixed inset-0 bg-black" />}>
          <ShaderBackground />
        </Suspense>
        <img
          src="/evinly_logo.svg"
          alt="Evinly"
          className="relative z-10 w-10 h-10 animate-pulse select-none"
          draggable={false}
        />
      </div>
    );
  }

  // ── Écran de vérification d'email ────────────────────────────────────
  // Placé AVANT le court-circuit `user` : quelqu'un déjà connecté qui clique
  // le lien doit voir le résultat, pas être renvoyé dans l'application.
  if (verifyState === 'pending') {
    if (pastel) {
      return (
        <div className="p-shell relative min-h-screen flex items-center justify-center overflow-hidden">
          <PastelPulseRing size={72} />
        </div>
      );
    }
    return (
      <div className="shader relative min-h-screen flex items-center justify-center overflow-hidden">
        <Suspense fallback={<div className="fixed inset-0 bg-black" />}>
          <ShaderBackground />
        </Suspense>
        <img src="/evinly_logo.svg" alt="Evinly" className="relative z-10 w-10 h-10 animate-pulse select-none" draggable={false} />
      </div>
    );
  }

  if (verifyState === 'ok' || verifyState === 'error') {
    const ok = verifyState === 'ok';
    return (
      <AuthLayout>
        <div className="text-center">
          <svg className={`w-12 h-12 mx-auto mb-4 ${ok ? 'text-emerald-400 pastel:text-emerald-600' : 'text-white/50 pastel:text-slate-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            {ok ? (
              <>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="m9 11 3 3L22 4" />
              </>
            ) : (
              <>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v5" />
                <path d="M12 16h.01" />
              </>
            )}
          </svg>
          <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-2">
            {ok ? 'Adresse confirmée' : 'Lien inutilisable'}
          </h2>
          <p className="text-sm text-white/60 pastel:text-slate-500 mb-6">
            {ok
              ? 'Votre compte est activé. Connectez-vous pour accéder à vos analyses.'
              : "Ce lien a déjà été utilisé, ou il a expiré. Essayez de vous connecter : si votre adresse est déjà confirmée, tout fonctionnera normalement."}
          </p>
          <button
            onClick={() => { setVerifyState('idle'); setView('login'); }}
            className="text-sm text-violet-300 pastel:text-violet-700 hover:underline"
          >
            {user ? "Retour à l'application" : 'Aller à la connexion'}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ── Changement d'adresse email : PocketBase exige le mot de passe ──────
  if (emailChangeToken || emailChangeState !== 'idle') {
    const handleConfirmEmailChange = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!emailChangeToken) return;
      setError(null);
      setLoading(true);
      try {
        await confirmEmailChange(emailChangeToken, password);
        setEmailChangeState('ok');
      } catch {
        setEmailChangeState('error');
      } finally {
        setEmailChangeToken(null);
        setPassword('');
        setLoading(false);
      }
    };
    if (emailChangeState !== 'idle') {
      const ok = emailChangeState === 'ok';
      return (
        <AuthLayout>
          <div className="text-center">
            <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-2">
              {ok ? 'Adresse email modifiée' : 'Changement impossible'}
            </h2>
            <p className="text-sm text-white/60 pastel:text-slate-500 mb-6">
              {ok
                ? 'Votre nouvelle adresse est active. Reconnectez-vous avec elle.'
                : 'Le lien a expiré ou le mot de passe était incorrect. Refaites la demande depuis votre compte.'}
            </p>
            <button
              onClick={() => { setEmailChangeState('idle'); if (ok) signOut(); setView('login'); }}
              className="text-sm text-violet-300 pastel:text-violet-700 hover:underline"
            >
              Aller à la connexion
            </button>
          </div>
        </AuthLayout>
      );
    }
    return (
      <AuthLayout>
        <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-1">Confirmer votre nouvelle adresse</h2>
        <p className="text-sm text-white/60 pastel:text-slate-500 mb-6">
          Saisissez votre mot de passe pour valider le changement d'adresse email.
        </p>
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleConfirmEmailChange} className="space-y-4">
          <Input label="Mot de passe" type="password" value={password} onChange={setPassword} autoFocus />
          <SubmitButton loading={loading}>Confirmer</SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  // Password recovery — the user followed a reset link. Sans session (le jeton
  // PocketBase n'authentifie pas), mais l'écran passe AVANT le court-circuit
  // `user` par cohérence avec l'ancien comportement.
  if (recoveryMode && !recoveryDone) {
    const handleNewPassword = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (newPassword.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Les deux mots de passe ne correspondent pas.');
        return;
      }
      setLoading(true);
      const result = await updatePassword(newPassword);
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setNewPassword('');
        setConfirmPassword('');
        setRecoveryDone(true);
      }
    };

    return (
      <AuthLayout>
        <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-1">Nouveau mot de passe</h2>
        <p className="text-sm text-white/60 pastel:text-slate-500 mb-6">
          Choisissez un nouveau mot de passe pour votre compte.
        </p>
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleNewPassword} className="space-y-4">
          <Input label="Nouveau mot de passe" type="password" value={newPassword} onChange={setNewPassword} autoFocus />
          <Input label="Confirmer le mot de passe" type="password" value={confirmPassword} onChange={setConfirmPassword} />
          <SubmitButton loading={loading}>Enregistrer le mot de passe</SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  // Password just reset — confirmation screen before entering the app.
  if (recoveryDone && !recoveryMode) {
    return (
      <AuthLayout>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-emerald-400 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="m9 11 3 3L22 4" />
          </svg>
          <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-2">Mot de passe mis à jour</h2>
          {/* PocketBase invalide tous les jetons existants lors d'un reset :
              l'utilisateur se reconnecte, il ne retombe pas dans l'app. */}
          <p className="text-sm text-white/60 pastel:text-slate-500 mb-6">
            Votre mot de passe a été modifié avec succès. Connectez-vous avec votre nouveau mot de passe.
          </p>
          <button
            onClick={() => { setRecoveryDone(false); setView('login'); setPassword(''); }}
            className="text-sm text-violet-300 pastel:text-violet-700 hover:underline"
          >
            Se connecter
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Session exists but needs MFA elevation — show challenge view before rendering the app
  if (mfaRequired) {
    const handleMfaSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (mfaCode.length !== 6 || !/^\d{6}$/.test(mfaCode)) {
        setError('Le code doit contenir exactement 6 chiffres.');
        return;
      }
      setLoading(true);
      const result = await verifyMfaChallenge(mfaCode);
      setLoading(false);
      if (result.error) {
        setError(result.error);
        setMfaCode('');
      }
    };

    return (
      <AuthLayout>
        <div className="text-center mb-4">
          <div className="inline-flex w-12 h-12 rounded-full bg-violet-500/20 pastel:bg-(--p-b-field) items-center justify-center mb-3">
            <svg className="w-6 h-6 text-violet-300 pastel:text-(--p-ink)" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-white pastel:text-slate-900 mb-1">Authentification à deux facteurs</h2>
          <p className="text-sm text-white/60 pastel:text-slate-500">
            Entrez le code à 6 chiffres généré par votre application d'authentification.
          </p>
        </div>

        {error && <ErrorBanner message={error} />}

        <form onSubmit={handleMfaSubmit} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            placeholder="000000"
            disabled={loading}
            className="w-full px-4 py-3 text-center text-2xl tracking-[0.4em] font-mono rounded-lg pastel:rounded-full border border-white/15 pastel:border-slate-300 bg-white/5 pastel:bg-white text-white pastel:text-slate-900 placeholder:text-white/30 pastel:placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
          />
          <SubmitButton loading={loading}>Vérifier</SubmitButton>
        </form>

        <p className="mt-6 text-center text-xs text-white/50 pastel:text-slate-500">
          Problème d'accès à votre application ?{' '}
          <button
            onClick={() => { setMfaCode(''); setError(null); signOut(); }}
            className="text-violet-300 pastel:text-violet-700 hover:underline"
          >
            Se déconnecter
          </button>
        </p>
      </AuthLayout>
    );
  }

  // Authenticated — render app
  if (user) {
    return <>{children}</>;
  }

  // ── Check email view ──
  if (view === 'check-email') {
    return (
      <AuthLayout>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-violet-500 shader:text-white mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white mb-2">Vérifiez votre email</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 mb-6">
            Un lien de confirmation a été envoyé à <strong className="shader:text-white">{email}</strong>. Cliquez dessus pour activer votre compte.
          </p>
          <button
            onClick={() => setView('login')}
            className="text-sm text-violet-600 dark:text-violet-400 shader:text-white shader:font-medium hover:underline"
          >
            Retour à la connexion
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ── Reset password view ──
  if (view === 'reset') {
    const handleReset = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);
      const result = await resetPassword(email);
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setView('check-email');
      }
    };

    return (
      <AuthLayout>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white mb-1">Réinitialiser le mot de passe</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 mb-6">
          Entrez votre email pour recevoir un lien de réinitialisation.
        </p>
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleReset} className="space-y-4">
          <Input label="Email" type="email" value={email} onChange={setEmail} autoFocus />
          <SubmitButton loading={loading}>Envoyer le lien</SubmitButton>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500 shader:text-white/80">
          <button onClick={() => { setView('login'); setError(null); }} className="text-violet-600 dark:text-violet-400 shader:text-white hover:underline">
            Retour à la connexion
          </button>
        </p>
      </AuthLayout>
    );
  }

  // ── OTP — inscription/connexion par code à 6 chiffres ──
  // Parcours « carte de visite » : email → code reçu → saisi sur la même page.
  // Aucun mot de passe à inventer, aucun aller-retour vers l'app mail, et
  // l'adresse reste vérifiée (donc exploitable après le salon).
  if (view === 'otp' || view === 'otp-verify') {
    const handleSendOtp = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!termsAccepted) {
        setError("Vous devez accepter les CGU et la politique de confidentialité pour continuer.");
        return;
      }
      setLoading(true);
      const result = await signInWithOtp(email, {
        termsAccepted: { version: LEGAL_VERSION, acceptedAt: new Date().toISOString() },
        promoCode: promoCode || undefined,
      });
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setOtpCode('');
        setOtpResent(false);
        setView('otp-verify');
      }
    };

    const submitOtp = async (code: string) => {
      if (loading) return;
      setError(null);
      // Ne pas figer une longueur exacte : la longueur du code OTP est un
      // réglage de la collection PocketBase (6 aujourd'hui, cf. migration),
      // pas une convention côté client. Verrouiller sur "6" pile a déjà cassé
      // le parcours une fois. PocketBase reste seul juge de la validité exacte
      // via authWithOTP — on ne fait ici qu'écarter une saisie manifestement
      // vide ou non numérique.
      if (!/^\d{4,10}$/.test(code)) {
        setError('Entrez le code reçu par email.');
        return;
      }
      setLoading(true);
      const result = await verifyEmailOtp(email, code);
      setLoading(false);
      if (result.error) {
        setError(result.error);
        setOtpCode('');
      }
      // Succès : onAuthStateChange ouvre la session, l'app se monte et
      // usePromoRedemption crédite les analyses offertes dans la foulée.
    };

    const handleVerifyOtp = (e: React.FormEvent) => {
      e.preventDefault();
      void submitOtp(otpCode);
    };

    // Le code arrive presque toujours par copier-coller depuis l'email : on
    // remplace la saisie par ce qui est collé et on valide dans la foulée,
    // sans passer par le bouton.
    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
      if (!pasted) return;
      e.preventDefault();
      setOtpCode(pasted);
      void submitOtp(pasted);
    };

    // Filet pour le remplissage automatique du navigateur (autocomplete
    // "one-time-code") : la valeur y arrive d'un bloc, sans événement paste.
    // Un bond de plus d'un caractère ne peut pas venir de la frappe manuelle.
    const handleOtpChange = (raw: string) => {
      const digits = raw.replace(/\D/g, '');
      const pasted = digits.length - otpCode.length > 1;
      setOtpCode(digits);
      if (pasted) void submitOtp(digits);
    };

    const handleResendOtp = async () => {
      setError(null);
      setLoading(true);
      const result = await signInWithOtp(email, {
        termsAccepted: { version: LEGAL_VERSION, acceptedAt: new Date().toISOString() },
        promoCode: promoCode || undefined,
      });
      setLoading(false);
      if (result.error) setError(result.error);
      else setOtpResent(true);
    };

    return (
      <AuthLayout>
        <PromoOffer info={promoInfo} code={promoCode} />

        {view === 'otp' ? (
          <>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white mb-1">
              {promoInfo?.valid ? 'Créez votre compte' : 'Connexion par email'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 mb-6">
              Entrez votre email : vous recevrez un code à 6 chiffres à saisir juste après.
              Pas de mot de passe à créer.
            </p>

            {error && <ErrorBanner message={error} />}

            <form onSubmit={handleSendOtp} className="space-y-4">
              <Input label="Email" type="email" value={email} onChange={setEmail} autoFocus />

              <label className="flex items-start gap-2.5 text-xs text-white/70 pastel:text-slate-600 leading-relaxed cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-white/20 pastel:border-slate-300 bg-white/5 pastel:bg-white text-violet-500 focus:ring-violet-500 focus:ring-offset-0 cursor-pointer flex-shrink-0"
                />
                <span>
                  J'ai lu et j'accepte les{' '}
                  <button type="button" onClick={() => setShowTerms(true)} className="underline text-white pastel:text-slate-900 hover:text-violet-300 pastel:hover:text-violet-700 transition-colors">
                    Conditions Générales d'Utilisation
                  </button>
                  {' '}et la{' '}
                  <button type="button" onClick={() => setShowPrivacy(true)} className="underline text-white pastel:text-slate-900 hover:text-violet-300 pastel:hover:text-violet-700 transition-colors">
                    Politique de confidentialité
                  </button>
                  {' '}d'Evinly.
                </span>
              </label>

              <SubmitButton loading={loading}>Recevoir mon code</SubmitButton>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white mb-1">
              Votre code
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 mb-6">
              Nous avons envoyé un code à 6 chiffres à <strong className="shader:text-white">{email}</strong>.
              Il est valable une heure.
            </p>

            {error && <ErrorBanner message={error} />}

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={10}
                value={otpCode}
                onChange={e => handleOtpChange(e.target.value)}
                onPaste={handleOtpPaste}
                autoFocus
                placeholder="Code reçu par email"
                disabled={loading}
                className="w-full px-4 py-3 text-center text-2xl tracking-[0.3em] font-mono rounded-lg pastel:rounded-full border border-white/15 pastel:border-slate-300 bg-white/5 pastel:bg-white text-white pastel:text-slate-900 placeholder:text-sm placeholder:tracking-normal placeholder:text-white/30 pastel:placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
              />
              <SubmitButton loading={loading}>Valider</SubmitButton>
            </form>

            <p className="mt-4 text-center text-xs text-white/60 pastel:text-slate-500">
              {otpResent ? (
                <span className="text-emerald-300 pastel:text-emerald-700">Nouveau code envoyé.</span>
              ) : (
                <>
                  Rien reçu ? Vérifiez vos spams ou{' '}
                  <button onClick={handleResendOtp} disabled={loading} className="text-violet-300 pastel:text-violet-700 hover:underline disabled:opacity-50">
                    renvoyez un code
                  </button>
                  .
                </>
              )}
            </p>
          </>
        )}

        <PrivacyPolicy visible={showPrivacy} onClose={() => setShowPrivacy(false)} />
        <TermsOfService visible={showTerms} onClose={() => setShowTerms(false)} />

        <p className="mt-6 text-center text-sm text-white pastel:text-slate-900">
          <button
            onClick={() => {
              setView(view === 'otp-verify' ? 'otp' : 'login');
              setError(null);
              setOtpCode('');
            }}
            className="text-white pastel:text-slate-900 hover:underline"
          >
            {view === 'otp-verify' ? "Modifier l'adresse email" : 'Utiliser un mot de passe'}
          </button>
        </p>
      </AuthLayout>
    );
  }

  // ── Login / Register views ──
  const isLogin = view === 'login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (isLogin) {
      const result = await signIn(email, password);
      setLoading(false);
      if (result.error) setError(result.error);
      // MFA handling will be added in Phase 3
    } else {
      if (password.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères.');
        setLoading(false);
        return;
      }
      if (!isJobOption(signupJob) || !isIndustryOption(signupIndustry)) {
        setError("Choisissez votre métier et votre secteur d'activité.");
        setLoading(false);
        return;
      }
      if (!termsAccepted) {
        setError("Vous devez accepter les CGU et la politique de confidentialité pour créer un compte.");
        setLoading(false);
        return;
      }
      const result = await signUp(
        email,
        password,
        { version: LEGAL_VERSION, acceptedAt: new Date().toISOString() },
        // Le visiteur venu du QR a pu préférer le mot de passe à l'OTP : le
        // code le suit quand même, y compris à travers le lien de confirmation.
        promoCode || undefined,
        { jobTitle: signupJob, industry: signupIndustry },
      );
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setView('check-email');
      }
    }
  };

  return (
    <AuthLayout>
      <PromoOffer info={promoInfo} code={promoCode} />

      <h2 className="text-lg font-semibold text-slate-900 dark:text-white shader:text-white mb-1">
        {isLogin ? 'Connexion' : 'Créer un compte'}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 mb-6">
        {isLogin
          ? 'Connectez-vous pour accéder à vos analyses.'
          : 'Créez votre compte pour commencer à analyser vos contenus.'}
      </p>

      {/* Google OAuth — flux popup du SDK PocketBase : la page n'est pas
          rechargée, l'authStore est renseigné au retour de la popup. Rendu
          uniquement si le fournisseur est configuré côté PocketBase. */}
      {googleEnabled && (
      <button
        onClick={() => { setError(null); void signInWithGoogle(); }}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg pastel:rounded-full pastel:bg-white border border-slate-300 dark:border-slate-600 shader:border-white/15 text-sm font-medium text-slate-700 dark:text-slate-200 shader:text-white hover:bg-slate-50 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continuer avec Google
      </button>
      )}

      {/* Connexion par code, au MÊME rang que Google et non en lien discret :
          les comptes créés au salon n'ont aucun mot de passe, c'est leur voie
          normale de retour. La reléguer sous le formulaire condamnait la
          moitié des inscrits à buter sur un champ qui ne les concerne pas. */}
      <button
        onClick={() => { setView('otp'); setError(null); setOtpCode(''); }}
        className="mt-3 w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg pastel:rounded-full pastel:bg-white border border-slate-300 dark:border-slate-600 shader:border-white/15 text-sm font-medium text-slate-700 dark:text-slate-200 shader:text-white hover:bg-slate-50 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
        Recevoir un code par email
        {lastMethod === 'otp' && (
          <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 shader:bg-white/15 shader:text-white">
            dernière fois
          </span>
        )}
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 shader:bg-white/10" />
        <span className="text-xs text-slate-400 dark:text-slate-500 shader:text-white/30">ou</span>
        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 shader:bg-white/10" />
      </div>

      {/* `oauthError` porte l'échec du flux Google (popup bloquée/fermée), que
          `signInWithGoogle` ne peut pas renvoyer avec sa signature `void`. */}
      {(error ?? oauthError) && <ErrorBanner message={error ?? oauthError!} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Email" type="email" value={email} onChange={setEmail} autoFocus />
        <Input label="Mot de passe" type="password" value={password} onChange={setPassword} />

        {!isLogin && (
          <>
            <Select
              label="Votre métier"
              placeholder="Choisissez votre métier"
              value={signupJob}
              onChange={setSignupJob}
              options={JOB_OPTIONS}
            />
            <Select
              label="Votre secteur d'activité"
              placeholder="Choisissez votre secteur"
              value={signupIndustry}
              onChange={setSignupIndustry}
              options={INDUSTRY_OPTIONS}
            />
          </>
        )}

        {/* Case à cocher CGU + Politique de confidentialité — uniquement à l'inscription */}
        {!isLogin && (
          <label className="flex items-start gap-2.5 text-xs text-white/70 pastel:text-slate-600 leading-relaxed cursor-pointer select-none">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/20 pastel:border-slate-300 bg-white/5 pastel:bg-white text-violet-500 focus:ring-violet-500 focus:ring-offset-0 cursor-pointer flex-shrink-0"
            />
            <span>
              J'ai lu et j'accepte les{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="underline text-white pastel:text-slate-900 hover:text-violet-300 pastel:hover:text-violet-700 transition-colors"
              >
                Conditions Générales d'Utilisation
              </button>
              {' '}et la{' '}
              <button
                type="button"
                onClick={() => setShowPrivacy(true)}
                className="underline text-white pastel:text-slate-900 hover:text-violet-300 pastel:hover:text-violet-700 transition-colors"
              >
                Politique de confidentialité
              </button>
              {' '}d'Evinly.
            </span>
          </label>
        )}

        <SubmitButton loading={loading}>
          {isLogin ? 'Se connecter' : "S'inscrire"}
        </SubmitButton>
      </form>

      {/* Modales légales accessibles avant l'inscription */}
      <PrivacyPolicy
        visible={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
      <TermsOfService
        visible={showTerms}
        onClose={() => setShowTerms(false)}
      />

      {isLogin && (
        <p className="mt-3 text-center text-xs text-white pastel:text-slate-900">
          <button onClick={() => { setView('reset'); setError(null); }} className="text-white pastel:text-slate-900 hover:underline">
            Mot de passe oublié ?
          </button>
        </p>
      )}

      <p className="mt-6 text-center text-sm text-white pastel:text-slate-900">
        {isLogin ? "Pas encore de compte ?" : 'Déjà un compte ?'}{' '}
        <button
          onClick={() => {
            setView(isLogin ? 'register' : 'login');
            setError(null);
            setTermsAccepted(false);
          }}
          className="text-white pastel:text-slate-900 hover:underline font-medium"
        >
          {isLogin ? "S'inscrire" : 'Se connecter'}
        </button>
      </p>
    </AuthLayout>
  );
}

// ── Sub-components ──────────────────────────────────────

/**
 * Bandeau d'offre affiché quand on arrive avec un code promo (QR d'une carte).
 *
 * Ne s'affiche QUE si le serveur a confirmé le code : un code expiré ou épuisé
 * ne doit rien annoncer du tout. Le visiteur voit alors l'inscription normale
 * — c'est le comportement voulu pour une carte pérenne retrouvée dans une
 * veste deux ans plus tard : le compte se crée quand même, sans promesse
 * qu'on ne pourrait pas tenir.
 */
function PromoOffer({ info, code }: { info: PromoCodePublicInfo | null; code: string }) {
  if (!info?.valid || !info.analyses) return null;
  return (
    <div className="mb-5 px-4 py-3 rounded-xl pastel:rounded-2xl bg-emerald-500/10 pastel:bg-emerald-100 border border-emerald-400/25 pastel:border-emerald-200 text-center">
      <p className="text-sm font-medium text-emerald-200 pastel:text-emerald-800">
        {info.analyses} analyse{info.analyses > 1 ? 's' : ''} offerte{info.analyses > 1 ? 's' : ''}
      </p>
      <p className="mt-0.5 text-xs text-emerald-100/70 pastel:text-emerald-700">
        Créditée{info.analyses > 1 ? 's' : ''} automatiquement avec le code{' '}
        <strong className="font-mono tracking-wide">{code}</strong>
      </p>
    </div>
  );
}

function AuthLayout({ children }: { children: ReactNode }) {
  // Mode pastel : split-screen sur crème, sans fond shader (cf. plus bas).
  const { theme } = useTheme();
  if (theme === 'pastel') return <PastelAuthLayout>{children}</PastelAuthLayout>;

  // Force the `shader` class on this subtree so every `shader:` variant below
  // activates, regardless of the user's app theme — the login screen always
  // uses the same shader background as the landing / waiting-list page.
  return (
    <div className="shader relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
      <Suspense fallback={<div className="fixed inset-0 bg-black" />}>
        <ShaderBackground />
      </Suspense>

      {/* Bascule de thème accessible AVANT la connexion : le mode pastel et ses
          nuanciers se choisissent aussi ici, pas seulement dans l'en-tête. */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggleButton className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-stretch justify-center gap-3 mb-8">
          <img
            src="/evinly_logo.svg"
            alt="Evinly"
            className="w-12 h-12 shrink-0 select-none"
            draggable={false}
          />
          <div className="flex flex-col justify-between py-0.5">
            <h1 className="text-3xl font-display font-normal text-white leading-none">Evinly</h1>
            <p className="text-[11px] text-white/40 leading-none">Conformité Loi Évin</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 shadow-lg shadow-black/30 p-6">
          {children}
        </div>

        <p className="mt-6 text-center text-[11px] text-white/30">
          Evinly ne se substitue pas à l'avis d'un avocat spécialisé.
        </p>
      </div>
    </div>
  );
}


/* ── Écran de connexion du mode pastel ───────────────────────────────
   Split-screen cousin de Visaly : à gauche une vitrine à l'aplat d'une
   teinte du nuancier (quatre volets qui tournent), à droite le formulaire
   sur crème, le tout dans le cadre à bordure encre. Motif « carrousel »
   du WAI : la rotation s'arrête définitivement dès que l'utilisateur
   prend la main (clic sur une pastille) ; survol et focus la mettent en
   pause ; sous `prefers-reduced-motion` elle ne démarre jamais. */
const PASTEL_SHOWCASE: { slot: 'a' | 'b' | 'c' | 'd'; shape: ShapeName; tab: string; title: string; lead: string }[] = [
  {
    slot: 'a', shape: 'bouteilleVin',
    tab: 'Score',
    title: 'Un score sur 100, avant diffusion',
    lead: 'Nom, slogan, étiquette ou campagne : le verdict tombe pendant que le message est encore modifiable.',
  },
  {
    slot: 'b', shape: 'cocktail',
    tab: '3 juges',
    title: 'Défense, contradicteur, arbitre',
    lead: 'Deux modèles plaident en sens inverse, un troisième tranche. Le score est un arbitrage, pas une moyenne.',
  },
  {
    slot: 'c', shape: 'carafe',
    tab: 'Sources',
    title: 'Chaque réserve renvoie à son article',
    lead: 'Article L3323-4 du Code de la santé publique et jurisprudence, consultés sur Légifrance.',
  },
  {
    slot: 'd', shape: 'verreVin',
    tab: 'Souveraineté',
    title: 'Vos briefs restent en Europe',
    lead: 'Traitement par Mistral, hébergement en Union européenne. Aucun entraînement de modèle sur vos données.',
  },
];

function PastelShowcase() {
  const [index, setIndex] = useState(0);
  const [taken, setTaken] = useState(false); // l'utilisateur a pris la main
  const [paused, setPaused] = useState(false); // survol / focus

  useEffect(() => {
    if (taken || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % PASTEL_SHOWCASE.length), 5000);
    return () => clearInterval(id);
  }, [taken, paused]);

  const item = PASTEL_SHOWCASE[index];

  return (
    <section
      aria-label="Aperçu d'Evinly"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className="relative flex flex-col justify-between overflow-hidden border-b-2 border-(--p-frame) p-6 transition-colors duration-700 lg:border-b-0 lg:border-r-2 lg:p-12"
      style={{ backgroundColor: `var(--p-${item.slot})` }}
    >
      {/* Contenant en filigrane — les formes géométriques de la charte de
          référence deviennent ici bouteilles, verres et carafes (ui/TypologyShape) */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-8 h-48 w-48 opacity-80 animate-shape-breathe lg:-right-14 lg:-top-10 lg:h-80 lg:w-80"
      >
        <TypologyShape shape={item.shape} color="rgba(255,255,255,0.55)" />
      </span>

      {/* Marque — sortie de secours pour qui arrive ici par erreur */}
      <a href="/" className="relative hidden items-center gap-2.5 lg:flex" aria-label="Evinly — accueil">
        <img src="/evinly_logo_blue.svg" alt="" aria-hidden className="h-8 w-8 select-none" draggable={false} />
        <span className="font-display text-2xl leading-none text-(--p-ink)">Evinly</span>
      </a>

      <div className="relative lg:max-w-md">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-(--p-ink)/55">
          {item.tab}
        </p>
        <h2 className="font-display text-2xl leading-tight text-(--p-ink) lg:text-[2.4rem]">
          {item.title}
        </h2>
        <p className="mt-3 hidden text-base leading-relaxed text-(--p-ink)/70 lg:block">
          {item.lead}
        </p>
      </div>

      {/* Pastilles de navigation — prendre la main arrête la rotation */}
      <div role="tablist" aria-label="Volets" className="relative mt-6 flex gap-2">
        {PASTEL_SHOWCASE.map((s, i) => (
          <button
            key={s.tab}
            role="tab"
            aria-selected={i === index}
            aria-label={s.tab}
            onClick={() => { setIndex(i); setTaken(true); }}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-(--p-ink)' : 'w-4 bg-(--p-ink)/25 hover:bg-(--p-ink)/45'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function PastelAuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="p-shell min-h-screen">
      <div className="p-frame grid min-h-screen overflow-hidden sm:min-h-[calc(100vh-1.5rem)] md:min-h-[calc(100vh-2rem)] lg:grid-cols-[1.05fr_.95fr]">
        <PastelShowcase />

        {/* Colonne formulaire */}
        <section className="relative flex flex-col justify-between gap-8 p-6 sm:p-10 lg:p-12">
          {/* Nuanciers, interrupteur sidebar et bascule de thème — les mêmes
              commandes que dans l'en-tête, pour tester avant de se connecter. */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
            <PaletteSwatches className="hidden sm:flex" />
            <ThemeToggleButton className="p-2 rounded-full text-(--p-ink) hover:bg-white/70 transition-colors" />
          </div>

          {/* Marque, version mobile (la vitrine ne l'affiche qu'en desktop) */}
          <a href="/" className="flex items-center gap-2.5 lg:hidden" aria-label="Evinly — accueil">
            <img src="/evinly_logo_blue.svg" alt="" aria-hidden className="h-7 w-7 select-none" draggable={false} />
            <span className="font-display text-xl leading-none text-[#2e5289]">Evinly</span>
          </a>

          {/* `flex-1` centre le formulaire dans la hauteur restante : sans lui,
              `justify-between` le collerait en haut de colonne sur grand écran. */}
          <div className="flex flex-1 items-center">
            <div className="mx-auto w-full max-w-sm">{children}</div>
          </div>

          <p className="mx-auto w-full max-w-sm text-[11px] leading-relaxed text-slate-400">
            Evinly est un outil d'aide à la décision. Il ne se substitue pas à l'avis d'un avocat spécialisé.
          </p>
        </section>
      </div>
    </div>
  );
}

function Input({ label, type, value, onChange, autoFocus }: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1.5">
        {label}
      </label>
      <div className="glow-input">
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoFocus={autoFocus}
          required
          className="w-full px-3 py-2 pastel:px-4 pastel:py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/5 text-sm text-slate-900 dark:text-white shader:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow"
        />
      </div>
    </div>
  );
}

function Select({ label, placeholder, value, onChange, options }: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 shader:text-white mb-1.5">
        {label}
      </label>
      <div className="glow-input">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          required
          className={`w-full px-3 py-2 pastel:px-4 pastel:py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/5 text-sm ${value ? 'text-slate-900 dark:text-white shader:text-white' : 'text-slate-400'} focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow [&>option]:text-slate-900 [&>option]:bg-white dark:[&>option]:text-white dark:[&>option]:bg-slate-800`}
        >
          <option value="" disabled>{placeholder}</option>
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function SubmitButton({ loading, children }: { loading: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full py-2.5 pastel:py-3 rounded-lg pastel:rounded-full text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shader:from-white shader:to-white shader:text-slate-900 shader:hover:from-white/90 shader:hover:to-white/90 pastel:bg-none pastel:bg-violet-500 pastel:hover:bg-violet-600 pastel:hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 transition-all"
    >
      {loading ? 'Chargement...' : children}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mb-4 px-3 py-2 pastel:px-4 pastel:py-2.5 rounded-lg pastel:rounded-2xl bg-red-50 dark:bg-red-900/20 shader:bg-red-500/10 border border-red-200 dark:border-red-800 shader:border-red-500/20 text-sm text-red-700 dark:text-red-400 shader:text-red-300">
      {message}
    </div>
  );
}
